import { CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { useAuth } from './auth/AuthContext';
import { AuthProvider } from './auth/AuthContext';
import { ErrorBoundary } from './auth/ErrorBoundary';
import { RequireAdmin } from './auth/RequireAdmin';
import { RequireClientMembership } from './auth/RequireClientMembership';
import { ActiveCompanyProvider } from './client/ActiveCompanyContext';
import { RequireClientUser } from './auth/RequireClientUser';
import { RequirePartnerAdmin } from './auth/RequirePartnerAdmin';
import { RequireWorker } from './auth/RequireWorker';
import { EnvBadge } from './components/EnvBadge';
import { PwaUpdatePrompt } from './components/PwaUpdatePrompt';
import { RouteFallback } from './components/RouteFallback';
import { ThemeColorMeta } from './components/ThemeColorMeta';
import { RoleSwitcher } from './dev/RoleSwitcher';
import { AdminShell } from './layout/AdminShell';
import { ClientShell } from './layout/ClientShell';
import { PartnerShell } from './layout/PartnerShell';
import { WorkerShell } from './layout/WorkerShell';
import { IS_DEV_PROJECT } from './services/environment';
import { addBreadcrumb, installErrorLogger, setCallerContextProvider } from './services/errorLog';
import { clientTheme } from './theme/clientTheme';
import { color as brandColor } from './theme/foundation';
import { theme } from './theme/theme';
import { workerTheme } from './theme/workerTheme';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

/** A redirect that keeps the query string and hash (e.g. `?objekt=`). */
function RedirectKeepingQuery({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

/**
 * Wires the errorLog logger to the live auth context (so logged errors
 * carry the caller's uid + role) and to React Router (so route changes
 * land in the breadcrumb buffer). Sits inside <AuthProvider> +
 * <BrowserRouter>; renders nothing.
 */
function ErrorLoggerBootstrap() {
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    installErrorLogger();
  }, []);

  useEffect(() => {
    setCallerContextProvider(() => ({
      uid: user?.uid,
      role: user?.role ?? undefined,
    }));
  }, [user]);

  useEffect(() => {
    addBreadcrumb('route_change', location.pathname);
  }, [location.pathname]);

  return null;
}

/**
 * Every route is its own chunk.
 *
 * These 50 pages used to be static imports, so one 3.3 MB bundle carried the
 * admin app, the client app and the worker app together — and the browser
 * parsed all three before showing any of them. That cost is paid on every
 * full page load, which is what the dev role-switcher does on each switch
 * (deliberately: swapping the Firebase Auth user under live onSnapshot
 * listeners corrupts the SDK's watch stream). A worker opening their day on
 * a phone paid it too.
 *
 * Named exports need the `{ default: m.X }` shim — `lazy` wants a module
 * whose default IS the component.
 */
const AdminMessagesPage = lazy(() => import('./pages/messages/AdminMessagesPage').then((m) => ({ default: m.AdminMessagesPage })));
const ClientDashboardPage = lazy(() => import('./pages/client/ClientDashboardPage').then((m) => ({ default: m.ClientDashboardPage })));
const AcceptInvitationPage = lazy(() => import('./pages/client/AcceptInvitationPage'));
const ClientDeliveredWorkPage = lazy(() => import('./pages/client/ClientDeliveredWorkPage').then((m) => ({ default: m.ClientDeliveredWorkPage })));
const ClientDetailPage = lazy(() => import('./pages/clients/ClientDetailPage').then((m) => ({ default: m.ClientDetailPage })));
const ClientInvoicesPage = lazy(() => import('./pages/client/ClientInvoicesPage').then((m) => ({ default: m.ClientInvoicesPage })));
const ClientMessagesPage = lazy(() => import('./pages/client/ClientMessagesPage').then((m) => ({ default: m.ClientMessagesPage })));
const ClientObjectKpiPage = lazy(() => import('./pages/client/ClientObjectKpiPage').then((m) => ({ default: m.ClientObjectKpiPage })));
const ClientsListPage = lazy(() => import('./pages/clients/ClientsListPage').then((m) => ({ default: m.ClientsListPage })));
const CompanyPage = lazy(() => import('./pages/client/CompanyPage').then((m) => ({ default: m.CompanyPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const ErrorsListPage = lazy(() => import('./pages/errors/ErrorsListPage').then((m) => ({ default: m.ErrorsListPage })));
const FeatureRequestsPage = lazy(() => import('./pages/FeatureRequestsPage').then((m) => ({ default: m.FeatureRequestsPage })));
const ForemanConfirmPage = lazy(() => import('./pages/foreman/ForemanConfirmPage').then((m) => ({ default: m.ForemanConfirmPage })));
const GuestOrderPage = lazy(() => import('./pages/client/GuestOrderPage').then((m) => ({ default: m.GuestOrderPage })));
const InvoiceBuilderPage = lazy(() => import('./pages/invoices/InvoiceBuilderPage').then((m) => ({ default: m.InvoiceBuilderPage })));
const InvoicesListPage = lazy(() => import('./pages/invoices/InvoicesListPage').then((m) => ({ default: m.InvoicesListPage })));
const JobDetailPage = lazy(() => import('./pages/jobs/JobDetailPage').then((m) => ({ default: m.JobDetailPage })));
const JobsListPage = lazy(() => import('./pages/jobs/JobsListPage').then((m) => ({ default: m.JobsListPage })));
const LeaveRequestsPage = lazy(() => import('./pages/leave/LeaveRequestsPage').then((m) => ({ default: m.LeaveRequestsPage })));
const LiveBoardPage = lazy(() => import('./pages/live/LiveBoardPage').then((m) => ({ default: m.LiveBoardPage })));
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const NewOrderEntry = lazy(() => import('./pages/client/B2bOrderPage').then((m) => ({ default: m.NewOrderEntry })));
const NoAccessPage = lazy(() => import('./pages/NoAccessPage').then((m) => ({ default: m.NoAccessPage })));
const OrderDetailPage = lazy(() => import('./pages/client/OrderDetailPage').then((m) => ({ default: m.OrderDetailPage })));
const OrdersListPage = lazy(() => import('./pages/client/OrdersListPage').then((m) => ({ default: m.OrdersListPage })));
const PartnerDetailPage = lazy(() => import('./pages/partners/PartnerDetailPage').then((m) => ({ default: m.PartnerDetailPage })));
const PartnerHomePage = lazy(() => import('./pages/partnerPortal/PartnerHomePage').then((m) => ({ default: m.PartnerHomePage })));
const PartnerInvoiceBuilderPage = lazy(() => import('./pages/partnerInvoices/PartnerInvoiceBuilderPage').then((m) => ({ default: m.PartnerInvoiceBuilderPage })));
const PartnerInvoicesListPage = lazy(() => import('./pages/partnerInvoices/PartnerInvoicesListPage').then((m) => ({ default: m.PartnerInvoicesListPage })));
const PartnerJobPage = lazy(() => import('./pages/partnerPortal/PartnerJobPage').then((m) => ({ default: m.PartnerJobPage })));
const PartnerOfferPage = lazy(() => import('./pages/partnerOffer/PartnerOfferPage').then((m) => ({ default: m.PartnerOfferPage })));
const PartnersListPage = lazy(() => import('./pages/partners/PartnersListPage').then((m) => ({ default: m.PartnersListPage })));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const RoadmapPage = lazy(() => import('./pages/RoadmapPage').then((m) => ({ default: m.RoadmapPage })));
const ScheduleBoardPage = lazy(() => import('./pages/schedule/ScheduleBoardPage').then((m) => ({ default: m.ScheduleBoardPage })));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const SignUpPage = lazy(() => import('./pages/SignUpPage').then((m) => ({ default: m.SignUpPage })));
const TimesheetsHubPage = lazy(() => import('./pages/timesheets/TimesheetsHubPage').then((m) => ({ default: m.TimesheetsHubPage })));
const UsersPage = lazy(() => import('./pages/UsersPage').then((m) => ({ default: m.UsersPage })));
const WorkerFirstRun = lazy(() => import('./pages/worker/WorkerFirstRun').then((m) => ({ default: m.WorkerFirstRun })));
const WorkerDashboardPage = lazy(() => import('./pages/worker/WorkerDashboardPage').then((m) => ({ default: m.WorkerDashboardPage })));
const WorkerDetailPage = lazy(() => import('./pages/workers/WorkerDetailPage').then((m) => ({ default: m.WorkerDetailPage })));
const WorkerJobDetailPage = lazy(() => import('./pages/worker/WorkerJobDetailPage').then((m) => ({ default: m.WorkerJobDetailPage })));
const WorkerJobsPage = lazy(() => import('./pages/worker/WorkerJobsPage').then((m) => ({ default: m.WorkerJobsPage })));
const WorkerLeavePage = lazy(() => import('./pages/worker/WorkerLeavePage').then((m) => ({ default: m.WorkerLeavePage })));
const WorkerMessagesPage = lazy(() => import('./pages/worker/WorkerMessagesPage').then((m) => ({ default: m.WorkerMessagesPage })));
const WorkerProfilePage = lazy(() => import('./pages/worker/WorkerProfilePage').then((m) => ({ default: m.WorkerProfilePage })));
const WorkerTimesheetsPage = lazy(() => import('./pages/worker/WorkerTimesheetsPage').then((m) => ({ default: m.WorkerTimesheetsPage })));
const WorkersListPage = lazy(() => import('./pages/workers/WorkersListPage').then((m) => ({ default: m.WorkersListPage })));
const WorkingTimeNormsPage = lazy(() => import('./pages/admin/WorkingTimeNormsPage').then((m) => ({ default: m.WorkingTimeNormsPage })));

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <ErrorBoundary>
          <ErrorLoggerBootstrap />
          {/* DEV-ONLY: floating role-switcher; renders null outside a dev build
              on the dev Firebase project (see dev/RoleSwitcher.tsx). */}
          <RoleSwitcher />
          {/* PWA-2: registers the app-shell service worker and prompts when a
              new build is waiting. Renders nothing until then. */}
          <PwaUpdatePrompt />
          {/* Names the Firebase project on every non-production build; renders
              null on the live site. See components/EnvBadge.tsx. */}
          <EnvBadge />
          <Suspense fallback={<RouteFallback />}>
<Routes>
            {/* ─── Public routes (admin theme by default) ───────────────── */}
            <Route
              path="/login"
              element={
                <ThemeProvider theme={theme}>
                  <CssBaseline />
                  <ThemeColorMeta color={theme.palette.primary.main} />
                  <LoginPage />
                </ThemeProvider>
              }
            />
            <Route
              path="/signup"
              element={
                <ThemeProvider theme={theme}>
                  <CssBaseline />
                  <ThemeColorMeta color={theme.palette.primary.main} />
                  <SignUpPage />
                </ThemeProvider>
              }
            />
            <Route
              path="/foreman-confirm/:token"
              element={
                <ThemeProvider theme={clientTheme}>
                  <CssBaseline />
                  <ThemeColorMeta color={brandColor.brandFill} />
                  <ForemanConfirmPage />
                </ThemeProvider>
              }
            />
            {/* EPIC 40 — public tokenized partner offer page. */}
            <Route
              path="/partner-offer/:token"
              element={
                <ThemeProvider theme={clientTheme}>
                  <CssBaseline />
                  <ThemeColorMeta color={brandColor.brandFill} />
                  <PartnerOfferPage />
                </ThemeProvider>
              }
            />
            <Route
              path="/no-access"
              element={
                <ThemeProvider theme={theme}>
                  <CssBaseline />
                  <ThemeColorMeta color={theme.palette.primary.main} />
                  <NoAccessPage />
                </ThemeProvider>
              }
            />
            {/* Public B2C guest checkout — no login (anonymous auth). */}
            <Route
              path="/order"
              element={
                <ThemeProvider theme={clientTheme}>
                  <CssBaseline />
                  <ThemeColorMeta color={brandColor.brandFill} />
                  <GuestOrderPage />
                </ThemeProvider>
              }
            />

            {/* ─── /client/* — client portal (client_user + admin) ──────── */}
            <Route
              path="/client/*"
              element={
                <ThemeProvider theme={clientTheme}>
                  <CssBaseline />
                  <ThemeColorMeta color={brandColor.brandFill} />
                  <RequireClientUser>
                    <RequireClientMembership>
                    {/* EPIC 54 P4 — which company the portal is showing. Every
                        screen below is scoped to one, and before this each
                        picked the first entry in `permissions` for itself. */}
                    <ActiveCompanyProvider>
                    <ClientShell>
                      <Suspense fallback={<RouteFallback />}>
<Routes>
                        <Route index element={<ClientDashboardPage />} />
                        {/* EPIC 54 P2 — where an invited user is sent until
                            they accept. Inside the shell so the language
                            switcher is there: §6.3 wants the overview shown
                            in the agreed language, and the agreement may be
                            exactly what they are about to correct. */}
                        <Route path="invitation" element={<AcceptInvitationPage />} />
                        {/* Stage 2.2 — "Objektid ja tegevused" is part of
                            Töölaud now; old links keep their query. */}
                        <Route path="my-work" element={<RedirectKeepingQuery to="/client" />} />
                        <Route path="new-order" element={<NewOrderEntry />} />
                        <Route path="orders" element={<OrdersListPage />} />
                        <Route path="orders/:id" element={<OrderDetailPage />} />
                        {/* "Praegu" is gone from the menu — it rendered the
                            same panels as the dashboard. The route redirects
                            rather than 404s, because a client may have it
                            bookmarked or linked in an email. */}
                        <Route path="live" element={<Navigate to="/client" replace />} />
                        <Route path="objects" element={<ClientObjectKpiPage />} />
                        <Route path="delivered" element={<ClientDeliveredWorkPage />} />
                        {/* EPIC 43 — old "Tunniarvestus" route → consolidated
                            "Tehtud tööd", opening on the approval segment. */}
                        <Route
                          path="timesheets"
                          element={<Navigate to="/client/delivered?vaade=kinnitamine" replace />}
                        />
                        <Route path="company" element={<CompanyPage />} />
                        <Route path="invoices" element={<ClientInvoicesPage />} />
                        {/* EPIC 47 — the client side of the inbox. Before this the
                            portal only had per-order message cards and nothing at
                            all for objects. */}
                        <Route path="messages" element={<ClientMessagesPage />} />
                        <Route path="*" element={<Navigate to="/client" replace />} />
                      </Routes>
</Suspense>
                    </ClientShell>
                    </ActiveCompanyProvider>
                    </RequireClientMembership>
                  </RequireClientUser>
                </ThemeProvider>
              }
            />

            {/* ─── /partner/* — partner portal (partner_admin + admin) ──── */}
            <Route
              path="/partner/*"
              element={
                <ThemeProvider theme={clientTheme}>
                  <CssBaseline />
                  <ThemeColorMeta color={brandColor.brandFill} />
                  <RequirePartnerAdmin>
                    <PartnerShell>
                      <Suspense fallback={<RouteFallback />}>
<Routes>
                        <Route index element={<PartnerHomePage />} />
                        <Route path="jobs/:id" element={<PartnerJobPage />} />
                        <Route path="*" element={<Navigate to="/partner" replace />} />
                      </Routes>
</Suspense>
                    </PartnerShell>
                  </RequirePartnerAdmin>
                </ThemeProvider>
              }
            />

            {/* ─── /worker/* — worker web shell (worker + admin) ────────── */}
            <Route
              path="/worker/*"
              element={
                <ThemeProvider theme={workerTheme}>
                  <CssBaseline />
                  <ThemeColorMeta color={brandColor.brandFill} />
                  <RequireWorker>
                    <Routes>
                      {/* UX 1A-e: the first run stands outside the shell. */}
                      <Route
                        path="welcome"
                        element={
                          <Suspense fallback={<RouteFallback />}>
                            <WorkerFirstRun />
                          </Suspense>
                        }
                      />
                      <Route
                        path="*"
                        element={
                          <WorkerShell>
                            <Suspense fallback={<RouteFallback />}>
                              <Routes>
                                <Route index element={<WorkerDashboardPage />} />
                                <Route path="jobs" element={<WorkerJobsPage />} />
                                <Route path="jobs/:id" element={<WorkerJobDetailPage />} />
                                <Route path="timesheets" element={<WorkerTimesheetsPage />} />
                                <Route path="messages" element={<WorkerMessagesPage />} />
                                <Route path="leave" element={<WorkerLeavePage />} />
                                <Route path="profile" element={<WorkerProfilePage />} />
                                <Route path="*" element={<Navigate to="/worker" replace />} />
                              </Routes>
                            </Suspense>
                          </WorkerShell>
                        }
                      />
                    </Routes>
                  </RequireWorker>
                </ThemeProvider>
              }
            />

            {/* ─── /* — operational admin (admin / dispatcher / finance) ─ */}
            <Route
              path="/*"
              element={
                <ThemeProvider theme={theme}>
                  <CssBaseline />
                  <ThemeColorMeta color={theme.palette.primary.main} />
                  <RequireAdmin>
                    <AdminShell>
                      <Suspense fallback={<RouteFallback />}>
<Routes>
                        <Route index element={<DashboardPage />} />
                        <Route path="users" element={<UsersPage />} />
                        <Route path="workers" element={<WorkersListPage />} />
                        <Route path="workers/:id" element={<WorkerDetailPage />} />
                        <Route path="leave-requests" element={<LeaveRequestsPage />} />
                        <Route path="messages" element={<AdminMessagesPage />} />
                        <Route path="clients" element={<ClientsListPage />} />
                        <Route path="clients/:id" element={<ClientDetailPage />} />
                        <Route path="partners" element={<PartnersListPage />} />
                        <Route path="partners/:id" element={<PartnerDetailPage />} />
                        <Route path="jobs" element={<JobsListPage />} />
                        <Route path="jobs/:id" element={<JobDetailPage />} />
                        {/* EPIC 44 — full-page resource-scheduling board. */}
                        <Route path="schedule" element={<ScheduleBoardPage />} />
                        {/* EPIC 44 Phase 2 — live "Praegu" (Now) ops board. */}
                        <Route path="live" element={<LiveBoardPage />} />
                        <Route path="timesheets" element={<TimesheetsHubPage />} />
                        {/* EPIC 43 — "Tehtud töö" merged into "Tehtud tööd";
                            keep the old route as a redirect to the overview. */}
                        <Route
                          path="delivered"
                          element={<Navigate to="/timesheets?vaade=ulevaade" replace />}
                        />
                        <Route path="invoices" element={<InvoicesListPage />} />
                        <Route path="invoices/new" element={<InvoiceBuilderPage />} />
                        <Route path="partner-invoices" element={<PartnerInvoicesListPage />} />
                        <Route path="partner-invoices/new" element={<PartnerInvoiceBuilderPage />} />
                        {/* EPIC 44 Phase 5b — hours/attendance reports + CSV export. */}
                        <Route path="reports" element={<ReportsPage />} />
                        {/* EPIC 48 — pricing + taxonomies consolidated into Settings. */}
                        <Route
                          path="pricing"
                          element={<Navigate to="/settings" replace />}
                        />
                        <Route
                          path="taxonomies"
                          element={<Navigate to="/settings?tab=skills" replace />}
                        />
                        {/* Tegevuskava is an INTERNAL tool. Its data source is
                            empty outside a dev-project build (see the
                            progressMarkdownPlugin in vite.config.ts), so the
                            page would render nothing on prod anyway — this
                            drops the route too, rather than showing an
                            empty timeline to an admin who navigates here. */}
                        {IS_DEV_PROJECT && (
                          <Route path="roadmap" element={<RoadmapPage />} />
                        )}
                        <Route path="feature-requests" element={<FeatureRequestsPage />} />
                        <Route path="settings" element={<SettingsPage />} />
                        <Route path="working-time-norms" element={<WorkingTimeNormsPage />} />
                        <Route path="errors" element={<ErrorsListPage />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
</Suspense>
                    </AdminShell>
                  </RequireAdmin>
                </ThemeProvider>
              }
            />
          </Routes>
</Suspense>
          </ErrorBoundary>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
